import React from 'react';
import {alpha, Box, Chip, Paper, Stack, Typography,} from '@mui/material';
import {Biotech, Build, Gavel,} from '@mui/icons-material';

const measurements = [{
    parameter: 'Density', value: '1.04', unit: 'g/mL',
}, {
    parameter: 'pH', value: '4.45', unit: '',
}, {
    parameter: 'Brix', value: '12.8', unit: '°Bx',
}, {
    parameter: 'Viscosity', value: '850', unit: 'cP',
}, {
    parameter: 'Moisture', value: '82.4', unit: '%',
},];

const insightCards = [{
//     title: 'Taste', icon: <Spa sx={{fontSize: 16}}/>, values: ['Creamy', 'Mildly sweet', 'Blueberry note'],
// }, {
//     title: 'Odor', icon: <Waves sx={{fontSize: 16}}/>, values: ['Dairy', 'Fruity', 'Fermented'],
// }, {
    title: 'Technical effect', icon: <Build sx={{fontSize: 16}}/>, values: ['Antimicrobial', 'Antioxidant', 'Texturizer'],
}, {
    title: 'E-number data', icon: <Gavel sx={{fontSize: 16}}/>, values: ['EU additive codes', 'E-number mapping'],
},];

function TechnicalSafetyDataVisual() {
    return (<Box
        sx={{
            width: '100%', height: '100%', minHeight: 300, p: 2, bgcolor: 'background.paper', display: 'flex', alignItems: 'center',
        }}
    >
        <Paper
            variant="outlined"
            sx={{
                width: '100%', borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper', boxShadow: '0 16px 32px rgba(15, 23, 42, 0.05)',
            }}
        >
            {/* Header */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    px: 2, py: 1.45, borderBottom: '1px solid', borderColor: 'divider',
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                        sx={{
                            width: 30, height: 30, borderRadius: '50%', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1), color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <Biotech sx={{fontSize: 17}}/>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{fontWeight: 800}}>
                            Technical and regulatory data
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{display: 'block', mt: 0.25}}
                        >
                            Physicochemical, sensory and regulatory profile
                        </Typography>
                    </Box>
                </Stack>

                <Chip
                    label="Technical profile"
                    size="small"
                    sx={{
                        height: 24, fontSize: 11, fontWeight: 800, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08), color: 'primary.main',
                    }}
                />
            </Stack>

            {/* Body */}
            <Box
                sx={{
                    display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '0.75fr 1fr'}, minHeight: 230
                }}
            >
                {/* Measurements table */}
                <Box sx={{p: 2}}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{mb: 1.25}}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4,
                            }}
                        >
                            Physicochemical measurements
                        </Typography>

                    </Stack>

                    <Paper
                        variant="outlined"
                        sx={{
                            borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper',
                        }}
                    >
                        {/* Table head */}
                        <Box
                            sx={{
                                display: 'grid', gridTemplateColumns: '1.35fr 0.9fr 0.7fr', columnGap: 1, px: 1.4, py: 1, bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04), borderBottom: '1px solid', borderColor: 'divider',
                            }}
                        >
                            {['Parameter', 'Measurement', 'Unit'].map((head) => (<Typography
                                key={head}
                                variant="caption"
                                color="text.secondary"
                                sx={{fontWeight: 800, fontSize: 10.5}}
                            >
                                {head}
                            </Typography>))}
                        </Box>

                        {/* Table rows */}
                        {measurements.map((row, index) => (<Box
                            key={row.parameter}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1.35fr 0.9fr 0.7fr',
                                columnGap: 1,
                                alignItems: 'center',
                                px: 1.4,
                                py: 1.05,
                                borderBottom: index !== measurements.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                bgcolor: index % 2 === 1 ? (theme) => alpha(theme.palette.grey[500], 0.025) : 'background.paper',
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 800, color: 'text.primary', fontSize: 11.5,
                                }}
                            >
                                {row.parameter}
                            </Typography>

                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 800, fontSize: 11.5,
                                }}
                            >
                                {row.value}
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    fontWeight: 700, fontSize: 11.5,
                                }}
                            >
                                {row.unit || '—'}
                            </Typography>
                        </Box>))}
                    </Paper>

                </Box>

                {/* Insights panel */}
                <Box
                    sx={{
                        p: 1.75, borderLeft: {sm: '1px solid rgba(15, 23, 42, 0.08)'}, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.018), display: 'flex', flexDirection: 'column', minHeight: 230,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block', mb: 1.1, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4,
                        }}
                    >
                        Ingredient insights
                    </Typography>

                    <Box
                        sx={{
                            flexGrow: 1, display: 'flex', alignItems: 'center', width: '100%', pb: 0.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, width: '100%',
                            }}
                        >
                            {insightCards.map((card) => (<Paper
                                    key={card.title}
                                    variant="outlined"
                                    sx={{
                                        p: 1.15, borderRadius: 2, bgcolor: 'background.paper', minHeight: 94,
                                    }}
                                >
                                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{mb: 0.75}}>
                                        <Box
                                            sx={{
                                                width: 23, height: 23, borderRadius: '50%', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.09), color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            }}
                                        >
                                            {card.icon}
                                        </Box>

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block', fontWeight: 800, color: 'text.primary', fontSize: 11, lineHeight: 1.15,
                                            }}
                                        >
                                            {card.title}
                                        </Typography>
                                    </Stack>

                                    <Stack spacing={0.45}>
                                        {card.values.slice(0, 3).map((value) => (<Typography
                                                key={value}
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: 'block', fontSize: 10.3, fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {value}
                                            </Typography>))}
                                    </Stack>
                                </Paper>))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Paper>
    </Box>);
}

export default TechnicalSafetyDataVisual;